import React, { useEffect, useState } from "react";
import { Card, Button, Typography, Row, Col, Divider, message } from "antd";
import { CheckCircle, ArrowLeft, Printer } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
// import { useWindowSize } from 'react-use'
import Confetti from "react-confetti";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const { Title, Text } = Typography;

const OceanPaymentSuccesspage = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(15);
  //   const { width, height } = useWindowSize()

 const htmlTemplate = `
<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<title>Payment Receipt</title>

<style>
    body {
        font-family: 'Inter', sans-serif;
        background: #f5f6f8;
        padding: 0;
        margin: 0;
    }

    /* A4 container – standard 794px width */
    .a4-page {
        width: 794px;
        margin: auto;
        background: white;
        padding: 40px 50px;
        box-sizing: border-box;
    }

    h1, h2, h3, h4 {
        margin: 0;
        padding: 0;
    }

    .header {
        border-bottom: 2px solid #0d9488;
        padding-bottom: 20px;
        margin-bottom: 40px;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .company-name {
        font-size: 26px;
        font-weight: 700;
        color: #0d9488;
    }

    .receipt-title {
        font-size: 32px;
        font-weight: 800;
        color: #333;
        text-align: right;
    }

    .section-title {
        font-size: 18px;
        font-weight: 600;
        margin-bottom: 10px;
        color: #333;
    }

    .info-box {
        margin-bottom: 30px;
        padding: 15px;
        border-radius: 8px;
        background: #f9fafb;
        border: 1px solid #e5e7eb;
    }

    .info-row {
        display: flex;
        justify-content: space-between;
        padding: 8px 0;
        border-bottom: 1px solid #ececec;
    }

    .info-row:last-child {
        border-bottom: none;
    }

    .label {
        color: #555;
        font-weight: 500;
    }

    .value {
        font-weight: 600;
        color: #222;
        text-align: right;
    }

    .footer {
        margin-top: 40px;
        padding-top: 20px;
        border-top: 1px solid #ddd;
        text-align: center;
        font-size: 13px;
        color: #777;
    }
</style>

</head>

<body>
    <div class="a4-page">

        <!-- HEADER -->
        <div class="header">
            <div class="company-name">PAYMENT RECEIPT</div>
            
        </div>

        <!-- CUSTOMER / RECEIPT INFO -->
        <div class="info-box">
            <div class="section-title">Transaction Summary</div>

            <div class="info-row">
                <div class="label">Amount Paid:</div>
                <div class="value">₹${state?.total_amount || "0"}</div>
            </div>

            <div class="info-row">
                <div class="label">Order ID:</div>
                <div class="value">${state?.orderid || "N/A"}</div>
            </div>

            <div class="info-row">
                <div class="label">Service:</div>
                <div class="value">FCL Shipment Booking</div>
            </div>

            <div class="info-row">
                <div class="label">Date & Time:</div>
                <div class="value">
                    ${state?.booking_confirm_date
                        ? dayjs(state.booking_confirm_date).format("DD-MM-YYYY hh:mm A")
                        : "N/A"
                    }
                </div>
            </div>
        </div>

        <!-- FOOTER -->
        <div class="footer">
            This is a system-generated receipt.  
            <br/>Thank you for choosing our services.
        </div>

    </div>
</body>
</html>
`;
  const handleDashboard = () => {
    message.info("Navigating to Dashboard...");
    navigate("/search-rates");
  };

  // -------- Print --------
const handleDownloadPDF = async () => {
  const wrapper = document.createElement("div");
  wrapper.innerHTML = htmlTemplate;
  document.body.appendChild(wrapper);

  const element = wrapper.querySelector(".a4-page");

  const canvas = await html2canvas(element, { scale: 2 }); // high DPI
  const imgData = canvas.toDataURL("image/png");

  const pdf = new jsPDF("p", "mm", "a4");
  const pdfWidth = pdf.internal.pageSize.getWidth();

  const imgWidth = pdfWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
  pdf.save("payment_receipt.pdf");

  document.body.removeChild(wrapper);
};

  // -------- Redirect Logic --------
  useEffect(() => {
    if (!state?.orderid) {
      message.error("Transaction details missing. Redirecting...");
      return navigate("/search-rates");
    }

    const timer = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(timer);
          navigate("/search-rates");
        }
        return c - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate, state]);
  return (
    <div className="flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 to-white">
      <Confetti width={1750} height={700} />
      <Card
        className="w-full max-w-5xl mx-auto shadow-lg rounded-xl border-t-4 border-emerald-500"
        // bodyStyle={{ padding: "32px" }}
      >
        <div className="flex flex-col items-center text-center">
          <div className="mb-6 p-4 rounded-full bg-emerald-50 text-emerald-500 shadow">
            <CheckCircle size={60} />
          </div>

          <Title level={2} className="!text-slate-800 font-bold">
            Payment Successful
          </Title>
          <Text className="text-gray-600 mb-4">
            Your payment has been processed successfully.
          </Text>

          <Text className="text-sm text-blue-600 font-semibold mb-4">
            Redirecting to Dashboard in {countdown}s...
          </Text>

          <Divider />

          {/* Transaction Summary */}
          <div className="w-full text-left space-y-3 p-4 rounded-lg bg-white border shadow-inner">
            <Title level={5} className="!text-slate-700 border-b pb-2">
              Transaction Summary
            </Title>

            <Row>
              <Col span={12} className="text-gray-500">
                Amount Paid:
              </Col>
              <Col span={12} className="font-bold text-emerald-600">
                ₹{state?.total_amount}
              </Col>
            </Row>

            <Row>
              <Col span={12} className="text-gray-500">
                Order ID:
              </Col>
              <Col
                xs={12}
                className="font-semibold text-gray-800 break-all"
                style={{ wordBreak: "break-all", whiteSpace: "normal" }}
              >
                {state?.orderid || "N/A"}
              </Col>
            </Row>

            <Row>
              <Col span={12} className="text-gray-500">
                Service:
              </Col>
              <Col span={12}>FCL Shipment Booking</Col>
            </Row>

            <Row>
              <Col span={12} className="text-gray-500">
                Date & Time:
              </Col>
              <Col span={12}>
                {state?.booking_confirm_date
                  ? dayjs(state.booking_confirm_date).format(
                      "DD-MM-YYYY hh:mm A"
                    )
                  : "N/A"}
              </Col>
            </Row>
          </div>

          <Divider />

          {/* Buttons */}
          <Row gutter={[16, 16]} className="w-full max-w-lg">
            <Col xs={24} md={12}>
              <Button
                type="default"
                size="large"
                onClick={handleDownloadPDF}
                className="w-full font-semibold rounded-lg text-blue-600 border-blue-200 hover:border-blue-400 hover:text-blue-700 transition duration-300"
              >
                Download Payment Receipt
              </Button>
            </Col>
            <Col xs={24} md={12}>
              <Button
                type="primary"
                size="large"
                onClick={handleDashboard}
                className="w-full bg-emerald-500 hover:!bg-emerald-600 !border-emerald-500 font-semibold rounded-lg shadow-lg shadow-emerald-200 transition duration-300"
              >
                Go to Dashboard
              </Button>
            </Col>
          </Row>
        </div>
      </Card>
    </div>
  );
};

export default OceanPaymentSuccesspage;
